import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { authService } from '../utils/auth';
import { toast } from 'sonner';
import { useFeatures } from '../context/FeaturesContext';
import { KeyRound, User, ArrowRight, AlertTriangle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const SWAMI_LOGO = '/swami-logo.png';

export default function Login() {
  const navigate = useNavigate();
  const { refreshFeatures } = useFeatures();
  const [mode, setMode] = useState('select'); // 'select', 'login', 'passkey'
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passkey, setPasskey] = useState('');
  const [loading, setLoading] = useState(false);
  const [logoutReason, setLogoutReason] = useState('');

  // Check for logout reason (when user was force-logged out due to being disabled)
  useEffect(() => {
    const reason = sessionStorage.getItem('logout_reason');
    if (reason) {
      setLogoutReason(reason);
      sessionStorage.removeItem('logout_reason');
      // Show toast after a brief delay
      setTimeout(() => {
        toast.error(reason);
      }, 500);
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await authService.login(username, password);
      await refreshFeatures();
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.message || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  const handlePasskeyValidate = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/auth/validate-passkey`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ passkey })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Invalid passkey');
      }
      
      // Store passkey and redirect to setup
      localStorage.setItem('pending_passkey', passkey);
      toast.success('Passkey validated! Complete your registration.');
      navigate('/tenant-setup');
    } catch (error) {
      toast.error(error.message || 'Invalid passkey');
    } finally {
      setLoading(false);
    }
  };

  if (mode === 'select') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3156] to-[#0A2540] flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
          <div className="absolute w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -bottom-48 -right-48 animate-pulse"></div>
        </div>

        <Card className="w-full max-w-md p-8 relative z-10 bg-white/95 backdrop-blur-sm shadow-2xl">
          {/* Show logout reason banner if user was force-logged out */}
          {logoutReason && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-red-800">Access Revoked</p>
                <p className="text-sm text-red-600 mt-1">{logoutReason}</p>
              </div>
            </div>
          )}

          <div className="text-center mb-8">
            <div className="mb-6 inline-block">
              <img src={SWAMI_LOGO} alt="Swami Software" className="w-24 h-24 object-contain mx-auto" />
            </div>
            <h1 className="text-2xl font-bold text-[#0A2540]">
              <span className="bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">RFQ</span>
              <span className="ml-1">Tracker</span>
            </h1>
            <p className="text-sm text-gray-500 mt-1">Manufacturing Cost Management</p>
          </div>

          <div className="space-y-4">
            <button
              onClick={() => setMode('passkey')}
              className="w-full p-4 border-2 border-orange-200 rounded-xl hover:border-orange-400 hover:bg-orange-50 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                  <KeyRound className="w-6 h-6 text-orange-600" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-semibold text-gray-800">Register with Passkey</h3>
                  <p className="text-sm text-gray-500">New user? Enter your passkey</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-orange-500 transition-colors" />
              </div>
            </button>

            <button
              onClick={() => setMode('login')}
              className="w-full p-4 border-2 border-blue-200 rounded-xl hover:border-blue-400 hover:bg-blue-50 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                  <User className="w-6 h-6 text-blue-600" />
                </div>
                <div className="text-left flex-1">
                  <h3 className="font-semibold text-gray-800">Login with Username</h3>
                  <p className="text-sm text-gray-500">Already registered? Sign in</p>
                </div>
                <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-blue-500 transition-colors" />
              </div>
            </button>
          </div>

          <div className="mt-6 text-center text-xs text-gray-400">
            <p>Powered by Swami Software</p>
          </div>
        </Card>
      </div>
    );
  }

  if (mode === 'passkey') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3156] to-[#0A2540] flex items-center justify-center p-4">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-orange-500/10 rounded-full blur-3xl -top-48 -right-48 animate-pulse"></div>
        </div>

        <Card className="w-full max-w-md p-8 relative z-10 bg-white/95 backdrop-blur-sm shadow-2xl">
          <button onClick={() => setMode('select')} className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1 text-sm">
            ← Back
          </button>
          
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <KeyRound className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="text-xl font-bold text-gray-800">Enter Your Passkey</h1>
            <p className="text-sm text-gray-500 mt-1">Provided by your administrator</p>
          </div>

          <form onSubmit={handlePasskeyValidate} className="space-y-4">
            <div>
              <Label htmlFor="passkey">Passkey</Label>
              <Input
                id="passkey"
                type="text"
                value={passkey}
                onChange={(e) => setPasskey(e.target.value.toUpperCase())}
                required
                className="mt-1 font-mono text-lg tracking-wider"
                placeholder="XXXX-XXXX-XXXX"
              />
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700"
              disabled={loading || !passkey}
            >
              {loading ? 'Validating...' : 'Continue'}
            </Button>
          </form>
        </Card>
      </div>
    );
  }

  // Login mode
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A2540] via-[#0d3156] to-[#0A2540] flex items-center justify-center p-4">
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl -top-48 -left-48 animate-pulse"></div>
      </div>

      <Card className="w-full max-w-md p-8 relative z-10 bg-white/95 backdrop-blur-sm shadow-2xl">
        <button onClick={() => setMode('select')} className="text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1 text-sm">
          ← Back
        </button>

        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <User className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-xl font-bold text-gray-800">Welcome Back</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to continue</p>
        </div>

        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <Label htmlFor="username">Username</Label>
            <Input
              id="username"
              data-testid="login-username"
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="mt-1"
              placeholder="Enter username"
            />
          </div>

          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="mt-1"
              placeholder="Enter password"
            />
          </div>

          <Button
            type="submit"
            data-testid="login-button"
            className="w-full bg-gradient-to-r from-[#0A2540] to-[#0d3156] hover:from-[#0d2f52] hover:to-[#0A2540]"
            disabled={loading}
          >
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
      </Card>
    </div>
  );
}
